'use client';

import Image from 'next/image';
import { PRODUCT_INFORMATION_MENU_ID } from '@headless-workspace/config';
import { useUIContext } from '@headless-workspace/core-ui';
import {
    getVariantImageClassNames,
    ProductDetailInfoCTA,
    ProductDetailSelectorMenu,
    ProductType,
} from '@headless-workspace/domain/common/client';
import {
    CapacityFormat,
    ProductDetailVariantValue,
    ProductSetItemValue,
} from '@headless-workspace/domain/common/server';
import {
    GlowIcon,
    GlowSelector,
    Icons,
    PropsWithFontFamily,
    PropsWithImageTagName,
    PropsWithLocale,
} from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { ProductSetList } from './ProductSetList';

export type VariantSelectorProps = PropsWithLocale &
    PropsWithFontFamily &
    PropsWithImageTagName & {
        productName?: string;
        variants: ProductDetailVariantValue[];
        formats: CapacityFormat[];
        currency: string;
        enableDiscountInfo?: boolean;
        priceAlwaysBlack: boolean;
        squaredShade: boolean;
        isVipBrand: boolean;
        type: ProductType;
        productsSetList?: ProductSetItemValue[];
        setHoveredVariant?: (variantId: string) => void;
        selectedVariantId: string;
        onVariantSelected: (variantId: string) => void;
        isPriceLegalMentionEnabled: boolean;
    };

export const VariantSelector: Component<VariantSelectorProps> = ({
    type,
    variants,
    formats,
    currency,
    enableDiscountInfo,
    priceAlwaysBlack,
    isVipBrand,
    squaredShade,
    locale,
    fontFamily,
    productsSetList,
    selectedVariantId,
    onVariantSelected,
    isPriceLegalMentionEnabled,
}) => {
    const {
        menuModal: { close: closeMenu },
    } = useUIContext();

    if (type === ProductType.PRODUCT_SET) {
        return (
            <ProductSetList
                productsList={productsSetList}
                fontFamily={fontFamily}
                priceAlwaysBlack={priceAlwaysBlack}
                isVipBrand={isVipBrand}
                squaredShade={squaredShade}
            />
        );
    }

    const { imageClassName, shapeClassName } = getVariantImageClassNames(type, squaredShade);

    const buttonElement = (openMenu: () => void) => {
        const hasMultipleVariants = variants.length > 1;
        const RightElement = hasMultipleVariants ? <GlowIcon Icon={Icons.ChevronRight} type={'medium'} /> : null;
        const onClick = hasMultipleVariants ? openMenu : undefined;

        const selectedVariant = variants.find((variant) => variant.id === selectedVariantId);

        return (
            <GlowSelector
                image={selectedVariant?.thumbnailImage}
                imageClassName={imageClassName}
                ImageTagName={Image}
                shapeClassName={shapeClassName}
                titleContent={{
                    label: selectedVariant?.name,
                    fontWeight: 'bold',
                }}
                RightElement={RightElement}
                onClick={onClick}
                fontFamily={fontFamily}
            />
        );
    };
    return (
        <ProductDetailInfoCTA
            menu={
                <ProductDetailSelectorMenu
                    type={type}
                    formats={formats}
                    variants={variants}
                    locale={locale}
                    currency={currency}
                    fontFamily={fontFamily}
                    enableDiscountInfo={enableDiscountInfo}
                    priceAlwaysBlack={priceAlwaysBlack}
                    squaredShade={squaredShade}
                    selectedVariantId={selectedVariantId}
                    onVariantSelected={(variantId) => {
                        onVariantSelected(variantId);
                        closeMenu(PRODUCT_INFORMATION_MENU_ID, true);
                    }}
                    isPriceLegalMentionEnabled={isPriceLegalMentionEnabled}
                />
            }
            buttonElement={buttonElement}
        />
    );
};
